import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { prepareCustomHeaders } from "../utils/baseQueryHeaders";
import { ProductId } from "../products/product";

// Types
export type EnrichResultId = number;
export type FieldsetId = number;
export type CustomerFieldId = number;
export type JobId = number;
export type TaskId = string;

export enum EnrichStatus {
  NOT_ENRICHED = "Not Enriched",
  PARTIALLY_ENRICHED = "Partially Enriched",
  ENRICHED = "Enriched",
  ENRICHED_AND_SENT_TO_PIM = "Enriched and sent to PIM",
  FAILED_TO_SEND_TO_PIM = "Failed to send to PIM",
}

export enum FieldType {
  STRING = "STRING",
  INTEGER = "INTEGER",
  DECIMAL = "DECIMAL",
  BOOLEAN = "BOOLEAN",
  LANGUAGE_STRING = "LANGUAGE_STRING",
  SINGLE_SELECT_LIST = "SINGLE_SELECT_LIST",
  MULTI_SELECT_LIST = "MULTI_SELECT_LIST",
}

export interface Fieldset {
  id: FieldsetId;
  name: string;
}

export interface ProductEnrichViewDataResponse {
  status: EnrichStatus;
  fieldsets: [FieldsetId, string][];
  product_fieldset: string | null;
  initial_fieldset_id: number | null;
  web_identification: Record<string, any>;
  image_urls: string[];
  supplier_text: string | null;
  supplier_text_field_id: string | null;
  latest_job_id: number | null;
  latest_job_url: string | null;
  job_is_active: boolean;
}

export interface EnrichedField {
  id: number;
  key: string;
  display_key: string;
  description: string | null;
  value: any;
  reasoning: string | null;
  type: FieldType | null;
  options: Record<string, string>;
  is_edited: boolean;
  published: string | null;
  send_error: string | null;
  customer_field_id: number | null;
  existing_pim_value: string | null;
}

export interface EnrichFromFieldsetRequest {
  product_id: ProductId;
  fieldset_name?: string;
  add_reasoning?: boolean;
  uploaded_files?: File[];
  /** When set, only these customer field IDs are enriched. Omit for all fields in the fieldset. */
  customer_field_ids?: number[];
}

export interface EnrichFromFieldsetResponse {
  job_id: JobId;
  job_detail_url: string;
}

export interface EnrichOneRequest {
  product_id: ProductId;
  customer_field_id: CustomerFieldId;
  add_reasoning?: boolean;
  uploaded_files?: File[];
}

export interface EnrichOneResponse {
  task_id: TaskId;
}

export interface UpdateEnrichResultFieldRequest {
  product_id: ProductId;
  enrich_result_id: EnrichResultId;
  field_value: any;
}

export interface RevertEnrichResultFieldRequest {
  product_id: ProductId;
  enrich_result_id: EnrichResultId;
}

export interface UpdateEnrichInputOverrideRequest {
  product_id: ProductId;
  web_identification?: Record<string, any>;
  supplier_text?: string | null;
}

export interface UpdateEnrichInputOverrideResponse {
  web_identification: Record<string, any>;
  supplier_text: string | null;
}

export interface SendEnrichToPimRequest {
  product_id: ProductId;
  enrich_result_ids?: EnrichResultId[];
}

export interface ResyncFromPimRequest {
  product_id: ProductId;
}

export interface ResyncFromPimResponse {
  message: string;
}

export interface ValidatedFieldsetField {
  key: string;
  customer_field_id: number | null;
  is_valid_field: boolean;
  error: string;
  value?: unknown;
  type?: string | null;
  options?: Record<string, string>;
}

export interface EnrichValidationRequest {
  fieldset_id: number;
  product_id?: ProductId;
}

// API definition
export const enrichApi = createApi({
  reducerPath: "enrichApi",
  tagTypes: ["EnrichData", "EnrichResult", "EnrichValidation"],
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/_internal/product",
    prepareHeaders: prepareCustomHeaders,
  }),
  endpoints: (build) => ({
    // Get enrich page data for a product
    getProductEnrichViewData: build.query<
      ProductEnrichViewDataResponse,
      ProductId
    >({
      query: (productId) => `/${productId}/enrich_view_data`,
      providesTags: (result, error, productId) => [
        { type: "EnrichData", id: productId },
      ],
    }),

    // Get latest enrich result for a product
    getEnrichResults: build.query<EnrichedField[] | null, ProductId>({
      query: (productId) => `/${productId}/enrich_results`,
      providesTags: (result, error, productId) => [
        { type: "EnrichResult", id: productId },
      ],
    }),

    getEnrichValidation: build.query<
      ValidatedFieldsetField[],
      EnrichValidationRequest
    >({
      query: ({ fieldset_id, product_id }) => {
        const suffix = product_id ? `/${product_id}` : "";
        return `/fieldset/${fieldset_id}/validate_enrich${suffix}`;
      },
      providesTags: (result, error, { product_id }) => [
        { type: "EnrichValidation", id: product_id },
      ],
    }),

    // Trigger enrichment from fieldset
    enrichFromFieldset: build.mutation<
      EnrichFromFieldsetResponse,
      EnrichFromFieldsetRequest
    >({
      query: ({
        product_id,
        uploaded_files,
        customer_field_ids,
        fieldset_name,
        ...body
      }) => {
        const formData = new FormData();
        if (fieldset_name) {
          formData.append("fieldset_name", fieldset_name);
        }
        formData.append("add_reasoning", String(body.add_reasoning ?? true));
        if (customer_field_ids != null) {
          for (const id of customer_field_ids) {
            formData.append("customer_field_ids", String(id));
          }
        }
        if (uploaded_files && uploaded_files.length > 0) {
          for (const file of uploaded_files) {
            formData.append("file", file, file.name);
          }
        }
        return {
          url: `/${product_id}/enrich_from_customer_fields`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result, error, { product_id }) => [
        { type: "EnrichResult", id: product_id },
        { type: "EnrichData", id: product_id },
      ],
    }),

    // Trigger enrichment for a single field
    enrichOne: build.mutation<EnrichOneResponse, EnrichOneRequest>({
      query: ({ product_id, uploaded_files, ...body }) => {
        const formData = new FormData();
        formData.append("customer_field_id", String(body.customer_field_id));
        formData.append("add_reasoning", String(body.add_reasoning ?? true));
        if (uploaded_files && uploaded_files.length > 0) {
          for (const file of uploaded_files) {
            formData.append("file", file, file.name);
          }
        }
        return {
          url: `/${product_id}/enrich_one`,
          method: "POST",
          body: formData,
        };
      },
    }),

    // Update a single field in enrich result
    updateEnrichResultField: build.mutation<
      EnrichedField,
      UpdateEnrichResultFieldRequest
    >({
      query: ({ product_id, enrich_result_id, field_value }) => ({
        url: `/${product_id}/enrich_result/${enrich_result_id}`,
        method: "PATCH",
        body: { field_value },
      }),
      invalidatesTags: (result, error, { product_id }) => [
        { type: "EnrichResult", id: product_id },
      ],
    }),

    // Revert a single field to original AI value
    revertEnrichResultField: build.mutation<
      EnrichedField,
      RevertEnrichResultFieldRequest
    >({
      query: ({ product_id, enrich_result_id }) => ({
        url: `/${product_id}/enrich_result/${enrich_result_id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { product_id }) => [
        { type: "EnrichResult", id: product_id },
      ],
    }),

    updateEnrichInputOverride: build.mutation<
      UpdateEnrichInputOverrideResponse,
      UpdateEnrichInputOverrideRequest
    >({
      query: ({ product_id, ...body }) => ({
        url: `/${product_id}/enrich_input_override`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, error, { product_id }) => [
        { type: "EnrichData", id: product_id },
      ],
    }),

    sendEnrichToPim: build.mutation<void, SendEnrichToPimRequest>({
      query: ({ product_id, enrich_result_ids }) => ({
        url: `/${product_id}/enrich_result/send_to_pim`,
        method: "POST",
        body: enrich_result_ids ? { enrich_result_ids } : undefined,
      }),
      invalidatesTags: (result, error, { product_id }) => [
        { type: "EnrichData", id: product_id },
        { type: "EnrichResult", id: product_id },
      ],
    }),

    resyncFromPim: build.mutation<ResyncFromPimResponse, ResyncFromPimRequest>({
      query: ({ product_id }) => ({
        url: `/${product_id}/resync_from_pim`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { product_id }) => [
        { type: "EnrichData", id: product_id },
        { type: "EnrichResult", id: product_id },
        { type: "EnrichValidation", id: product_id },
      ],
    }),
  }),
});

export const {
  useGetProductEnrichViewDataQuery,
  useGetEnrichResultsQuery,
  useGetEnrichValidationQuery,
  useEnrichFromFieldsetMutation,
  useEnrichOneMutation,
  useUpdateEnrichResultFieldMutation,
  useRevertEnrichResultFieldMutation,
  useUpdateEnrichInputOverrideMutation,
  useSendEnrichToPimMutation,
  useResyncFromPimMutation,
} = enrichApi;
